/**
 * Zoom Integration settings panel (workspace-scoped S2S credentials).
 * Layout aligned with the Basecamp settings panel.
 */
import { useState } from 'react'
import {
  AlertTriangle,
  Check,
  ChevronDown,
  ChevronUp,
  ExternalLink,
  Eye,
  EyeOff,
  Loader2,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { fetchWithAuth, API_BASE_URL } from '@/lib/api'
import { testIds } from '@/lib/test-ids'
import { toast } from 'sonner'
import { useZoomAuth } from '@/hooks/use-zoom-auth'

interface ConnectForm {
  accountId: string
  clientId: string
  clientSecret: string
  webhookSecretToken: string
}

interface ZoomIntegrationSectionProps {
  organizationId?: string | null
  workspaceName?: string | null
}

function orgQs(organizationId?: string | null): string {
  if (!organizationId) return ''
  return `?organizationId=${encodeURIComponent(organizationId)}`
}

function humanZoomError(raw?: string): string {
  if (!raw) return 'Zoom rejected the stored credentials.'
  if (raw.includes('invalid_client') || raw.includes('disabled by the developer')) {
    return 'Zoom rejected Account ID / Client ID / Client Secret (invalid_client). Marketplace may show Activated while the secret values in Maater are wrong or stale.'
  }
  return raw
}

export function ZoomIntegrationSection({
  organizationId,
  workspaceName,
}: ZoomIntegrationSectionProps) {
  const { authStatus, isLoading, refetch, disconnect } = useZoomAuth(organizationId)
  const [showSetup, setShowSetup] = useState(false)
  const [saving, setSaving] = useState(false)
  const [disconnecting, setDisconnecting] = useState(false)
  const [testing, setTesting] = useState(false)
  const [showSecret, setShowSecret] = useState(false)
  const [showWebhookSecret, setShowWebhookSecret] = useState(false)
  const [form, setForm] = useState<ConnectForm>({
    accountId: '',
    clientId: '',
    clientSecret: '',
    webhookSecretToken: '',
  })

  const isConnected = authStatus?.connected === true
  const hasCredentialError =
    !isLoading && !isConnected && Boolean(authStatus?.configured && authStatus?.error)
  const missingSecrets =
    !isLoading && !isConnected && authStatus?.missing?.length ? authStatus.missing : []
  const webhookUrl = `${API_BASE_URL.replace(/\/$/, '')}/api/zoom/webhook`

  const handleSave = async () => {
    if (!form.accountId || !form.clientId || !form.clientSecret) {
      toast.error('Account ID, Client ID, and Client Secret are required')
      return
    }
    setSaving(true)
    try {
      const res = await fetchWithAuth(`/api/zoom/auth/connect${orgQs(organizationId)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          organizationId: organizationId || undefined,
          accountId: form.accountId.trim(),
          clientId: form.clientId.trim(),
          clientSecret: form.clientSecret.trim(),
          webhookSecretToken: form.webhookSecretToken.trim() || undefined,
        }),
      })
      const data = await res.json()
      if (!res.ok) {
        toast.error(data.error || 'Failed to save credentials', {
          description: humanZoomError(
            typeof data.detail === 'string' ? data.detail : data.error,
          ),
        })
        return
      }
      toast.success('Zoom credentials saved', {
        description: workspaceName
          ? `Validated and encrypted for ${workspaceName}.`
          : 'Validated and encrypted in workspace secrets.',
      })
      setShowSetup(false)
      setForm({ accountId: '', clientId: '', clientSecret: '', webhookSecretToken: '' })
      await refetch()
    } catch {
      toast.error('Could not reach the Maater API')
    } finally {
      setSaving(false)
    }
  }

  const handleDisconnect = async () => {
    setDisconnecting(true)
    try {
      const ok = await disconnect()
      if (ok) {
        toast.success('Zoom disconnected')
        await refetch()
      } else {
        toast.error('Could not disconnect Zoom')
      }
    } catch {
      toast.error('Could not reach the Maater API')
    } finally {
      setDisconnecting(false)
    }
  }

  const handleTest = async () => {
    setTesting(true)
    try {
      const status = await refetch()
      if (!status) {
        toast.error('Could not check Zoom status')
        return
      }
      if (!status.configured) {
        toast.error('Credentials not configured', {
          description: status.missing?.length
            ? `Missing: ${status.missing.join(', ')}`
            : 'Enter Account ID, Client ID, and Client Secret below.',
        })
        return
      }
      if (!status.connected) {
        toast.error('Connection test failed', {
          description: status.hint || humanZoomError(status.error),
        })
        return
      }

      const listRes = await fetchWithAuth(`/api/zoom/recordings${orgQs(organizationId)}`)
      const listData = await listRes.json().catch(() => ({}))
      if (!listRes.ok) {
        toast.error('Token OK, but listing recordings failed', {
          description:
            typeof listData?.error === 'string' ? listData.error : listRes.statusText,
        })
        return
      }
      const total = typeof listData.total === 'number' ? listData.total : (listData.meetings?.length ?? 0)
      toast.success('Zoom connection OK', {
        description: `${total} cloud recording(s) in the last 7 days${
          workspaceName ? ` (${workspaceName})` : ''
        }.`,
      })
    } catch {
      toast.error('Could not reach the Maater API')
    } finally {
      setTesting(false)
    }
  }

  return (
    <div
      className="rounded-lg border border-border bg-muted/20 px-6 py-6"
      data-testid={testIds.workspaceSettings.zoomPanel}
    >
      <div className="mb-1 flex items-baseline justify-between gap-3">
        <h2 className="text-2xl font-semibold tracking-tight">Zoom</h2>
        <span className="text-xs text-muted-foreground">
          {isLoading ? 'Checking…' : isConnected ? 'Enabled' : 'Disabled'}
        </span>
      </div>
      <p className="text-sm leading-relaxed text-muted-foreground">
        Auto-capture meeting transcripts for this workspace when cloud recording finishes. Credentials
        stay in encrypted workspace secrets - not Railway env vars.
      </p>

      <div className="mt-6 space-y-6 border-t border-border pt-6">
        {isLoading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Checking Zoom connection…
          </div>
        ) : isConnected ? (
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-2 text-sm text-emerald-700 dark:text-emerald-400">
              <Check className="h-4 w-4 shrink-0" />
              <span>S2S credentials valid</span>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={testing}
                onClick={() => void handleTest()}
                data-testid={testIds.workspaceSettings.zoomValidateButton}
              >
                {testing ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Test connection'}
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={disconnecting}
                onClick={() => void handleDisconnect()}
                data-testid={testIds.workspaceSettings.zoomDisconnectButton}
              >
                {disconnecting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Disconnect'}
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {missingSecrets.length > 0 && (
              <div className="flex items-start gap-2 text-sm text-amber-700 dark:text-amber-400">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                <span>
                  Not connected. Missing secrets:{' '}
                  <code className="font-mono text-xs">{missingSecrets.join(', ')}</code>
                </span>
              </div>
            )}
            {hasCredentialError && (
              <div className="space-y-2 rounded-md border border-red-500/20 bg-red-500/10 p-3 text-sm">
                <div className="flex items-start gap-2 text-red-700 dark:text-red-400">
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                  <div className="space-y-1">
                    <p className="font-medium">Credentials invalid</p>
                    <p className="text-xs text-muted-foreground">
                      {humanZoomError(authStatus?.error)}
                    </p>
                    {authStatus?.hint ? (
                      <p className="text-xs text-muted-foreground">{authStatus.hint}</p>
                    ) : null}
                  </div>
                </div>
              </div>
            )}
            {!missingSecrets.length && !hasCredentialError && (
              <div className="flex items-start gap-2 text-sm text-amber-700 dark:text-amber-400">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                <span>
                  Not connected. Save Server-to-Server credentials for
                  {workspaceName ? ` ${workspaceName}` : ' this workspace'}.
                </span>
              </div>
            )}
            <div className="flex flex-wrap items-center gap-2">
              <Button
                size="sm"
                onClick={() => setShowSetup(true)}
                data-testid={testIds.workspaceSettings.zoomConnectButton}
              >
                Connect Zoom
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={testing || (!authStatus?.configured && missingSecrets.length > 0)}
                onClick={() => void handleTest()}
                data-testid={testIds.workspaceSettings.zoomValidateButton}
              >
                {testing ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Test connection'}
              </Button>
            </div>
          </div>
        )}

        {isConnected && (
          <div className="space-y-3 border-t border-border pt-6">
            <div>
              <p className="text-xs font-medium text-muted-foreground">Webhook endpoint</p>
              <p className="mt-1 break-all font-mono text-xs text-muted-foreground">{webhookUrl}</p>
            </div>
            <p className="text-xs text-muted-foreground">
              Zoom Marketplace event subscription should point at this URL for{' '}
              <code className="font-mono">recording.transcript_completed</code>.
            </p>
          </div>
        )}

        <div className="border-t border-border pt-4">
          <button
            type="button"
            className="flex items-center gap-1 text-sm text-muted-foreground transition-colors hover:text-foreground"
            onClick={() => setShowSetup((v) => !v)}
          >
            {showSetup ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            {isConnected ? 'Update credentials' : 'Set up Zoom connection'}
          </button>

          {showSetup && (
            <div className="mt-4 space-y-4">
              <div className="space-y-3 rounded-md border bg-background/50 p-4 text-sm">
                <p className="font-medium">Setup steps</p>
                <ol className="list-decimal list-inside space-y-2 text-muted-foreground">
                  <li>
                    Open{' '}
                    <a
                      href="https://marketplace.zoom.us/develop/create"
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1 text-blue-500 hover:underline"
                    >
                      Zoom Marketplace <ExternalLink className="h-3 w-3" />
                    </a>{' '}
                    and create a <strong>Server-to-Server OAuth</strong> app (or open Maater AI)
                  </li>
                  <li>
                    App Credentials: copy <strong>Account ID</strong>, <strong>Client ID</strong>, and{' '}
                    <strong>Client Secret</strong> fresh (re-copy if you see invalid_client)
                  </li>
                  <li>
                    Feature tab: Event Subscriptions → delivery URL{' '}
                    <code className="rounded bg-muted px-1 py-0.5 font-mono text-xs">{webhookUrl}</code>
                    , event <code className="rounded bg-muted px-1 py-0.5 font-mono text-xs">Recording Transcript files have completed</code>
                  </li>
                  <li>Activation tab must show Live / Activated on the Hop Zoom account</li>
                  <li>
                    Account Settings → Recording: Cloud recording ON and Audio transcript ON
                  </li>
                </ol>
              </div>

              <div className="space-y-3">
                <p className="text-sm font-medium">
                  Enter credentials
                  {workspaceName ? (
                    <span className="font-normal text-muted-foreground">
                      {' '}
                      for {workspaceName}
                    </span>
                  ) : null}
                </p>
                <div className="space-y-2">
                  <div>
                    <Label className="text-xs">Account ID</Label>
                    <Input
                      value={form.accountId}
                      onChange={(e) => setForm((f) => ({ ...f, accountId: e.target.value }))}
                      placeholder="From App Credentials"
                      className="font-mono text-sm"
                      autoComplete="off"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Client ID</Label>
                    <Input
                      value={form.clientId}
                      onChange={(e) => setForm((f) => ({ ...f, clientId: e.target.value }))}
                      placeholder="From App Credentials"
                      className="font-mono text-sm"
                      autoComplete="off"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Client Secret</Label>
                    <div className="relative">
                      <Input
                        type={showSecret ? 'text' : 'password'}
                        value={form.clientSecret}
                        onChange={(e) => setForm((f) => ({ ...f, clientSecret: e.target.value }))}
                        placeholder="From App Credentials"
                        className="pr-9 font-mono text-sm"
                        autoComplete="new-password"
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="absolute right-1 top-1/2 h-7 w-7 -translate-y-1/2"
                        onClick={() => setShowSecret((v) => !v)}
                        tabIndex={-1}
                      >
                        {showSecret ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                      </Button>
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs">
                      Webhook Secret Token{' '}
                      <span className="font-normal text-muted-foreground">(Feature tab)</span>
                    </Label>
                    <div className="relative">
                      <Input
                        type={showWebhookSecret ? 'text' : 'password'}
                        value={form.webhookSecretToken}
                        onChange={(e) =>
                          setForm((f) => ({ ...f, webhookSecretToken: e.target.value }))
                        }
                        placeholder="Secret Token from Event Subscription"
                        className="pr-9 font-mono text-sm"
                        autoComplete="new-password"
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="absolute right-1 top-1/2 h-7 w-7 -translate-y-1/2"
                        onClick={() => setShowWebhookSecret((v) => !v)}
                        tabIndex={-1}
                      >
                        {showWebhookSecret ? (
                          <EyeOff className="h-3.5 w-3.5" />
                        ) : (
                          <Eye className="h-3.5 w-3.5" />
                        )}
                      </Button>
                    </div>
                  </div>
                </div>
                <Button
                  size="sm"
                  onClick={() => void handleSave()}
                  disabled={saving || !form.accountId || !form.clientId || !form.clientSecret}
                >
                  {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                  {isConnected ? 'Update credentials' : 'Save and connect'}
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
